/**
 * Motorista — a tela do celular, na rua.
 *
 * Dois momentos, e só isso: RETIRAR no galpão e ENTREGAR na obra. Cada um é uma
 * foto e um toque.
 *
 * O QUE MUDA EM RELAÇÃO AO SISTEMA ATUAL: o estoque baixa na RETIRADA, não na
 * entrega (decisão do Lucas em 03/08). A tela diz isso em cada estado, porque
 * muda o significado de "em rota" — o material já saiu do número, não está mais
 * disponível para outro pedido.
 *
 * ACESSO SEM LOGIN: o motorista terceirizado abre um link de WhatsApp com
 * `?rt=<token>` e cai direto no romaneio dele. Não tem conta Google, não tem
 * cadastro, e forçar login aí seria pior que o sistema atual — que resolve isso
 * bem. O token dá acesso a UMA entrega, e nada mais.
 *
 * ENDEREÇO COM LINK DE MAPA: é a informação que evita a ligação "onde é a
 * obra?". Por isso aparece grande e clicável nos dois momentos.
 */

import { api, ErroApi } from '../api.js';
import { escapar, quando, dataHora, numero } from '../comum/formato.js';
import { avisar, avisarBloqueante } from '../comum/aviso.js';
import { ativarPush, jaInscrito, suportaPush } from '../comum/push.js';

type Status = 'aguardando_retirada' | 'em_rota' | 'entregue' | 'cancelado';

interface RomaneioLista {
  numero: string;
  status: Status;
  criado_em: string;
  retirado_em: string | null;
  entregue_em: string | null;
  /** Nulos em romaneio de CONCERTO — o destino é a loja, não uma obra. */
  obra: string | null;
  obraNome: string | null;
  obraEndereco: string | null;
  finalidade: 'obra' | 'concerto';
  itens: number;
}

interface FerramentaDoConcerto {
  id: number;
  patrimonio: string;
  numero_serie: string | null;
  itemNome: string;
  defeito: string | null;
  empresa_motorista: string | null;
  deixado_em: string | null;
}

interface ItemRomaneio {
  codigo: string;
  nome: string;
  unidade: string | null;
  quantidade: number;
}

interface RomaneioDetalhe extends Omit<RomaneioLista, 'itens'> {
  encarregado: string | null;
  motorista_nome: string | null;
  recebedor: string | null;
  obs: string | null;
  itens: ItemRomaneio[];
  ferramentas: FerramentaDoConcerto[];
  foto_retirada_url: string | null;
  fotosEntrega: string[];
}

export function montarMotorista(raiz: HTMLElement): void {
  let aberto: string | null = null;

  raiz.innerHTML = `
    <div id="mot-avisos"></div>
    <div class="chips" id="filtros">
      <button class="chip" data-f="andamento" aria-pressed="true">Em andamento</button>
      <button class="chip" data-f="finalizados" aria-pressed="false">✓ Finalizados</button>
      <button class="chip" id="mot-abastecer">⛽ Abastecimento</button>
    </div>
    <div id="lista"><p class="carregando">Carregando…</p></div>`;

  const lista = raiz.querySelector<HTMLElement>('#lista')!;
  let aba: 'andamento' | 'finalizados' = 'andamento';

  /**
   * OS AVISOS, na entrada do módulo: romaneio novo designado a mim e
   * manutenção do meu veículo vencendo. Mostrar é também MARCAR como visto —
   * o aviso avisa uma vez, não vira paisagem.
   */
  void api
    .get<{
      romaneiosNovos: { numero: string; obraNome: string | null; finalidade: string }[];
      veiculo: { placa: string; descricao: string | null; km_atual: number } | null;
      manutencoes: { tipo: string; proximo_km: number | null; proxima_data: string | null; placa: string }[];
      oleoPneus?: { tipo: string; detalhe: string }[];
    }>('/api/motorista/avisos')
    .then((av) => {
      const caixa = raiz.querySelector<HTMLElement>('#mot-avisos')!;
      const blocos: string[] = [];
      if (av.romaneiosNovos.length) {
        blocos.push(`<div class="mot-aviso novo">🚚 <b>Romaneio novo para você:</b> ${av.romaneiosNovos
          .map((r) => `${escapar(r.numero)} (${escapar(r.obraNome ?? 'concerto de ferramentas')})`)
          .join(' · ')}</div>`);
        void api
          .post('/api/motorista/avisos/romaneios-vistos', {
            numeros: av.romaneiosNovos.map((r) => r.numero),
          })
          .catch(() => {});
      }
      for (const op of av.oleoPneus ?? []) {
        blocos.push(`<div class="mot-aviso manutencao">🛢️ <b>${escapar(op.tipo)}:</b> ${escapar(op.detalhe)} — avise o CD.</div>`);
      }
      for (const m of av.manutencoes) {
        blocos.push(`<div class="mot-aviso manutencao">🔧 <b>${escapar(m.placa)}:</b> ${escapar(m.tipo)} vencendo${
          m.proximo_km ? ` (${m.proximo_km.toLocaleString('pt-BR')} km)` : ''
        } — avise o CD.</div>`);
      }
      caixa.innerHTML = blocos.join('');
    })
    .catch(() => {});

  raiz.querySelector('#mot-abastecer')!.addEventListener('click', () => void abastecer());

  // O convite para o PUSH DE VERDADE — só aparece se o navegador suporta e a
  // pessoa ainda não ativou. No iPhone é preciso "Adicionar à Tela de Início"
  // antes (regra da Apple); o texto explica quando for o caso.
  if (suportaPush()) {
    void jaInscrito().then((sim) => {
      if (sim || Notification.permission === 'denied') return;
      const caixa = raiz.querySelector<HTMLElement>('#mot-avisos')!;
      const convite = document.createElement('div');
      convite.className = 'mot-aviso';
      convite.innerHTML = `🔔 Receba os avisos <b>no celular, mesmo com o app fechado</b>
        (romaneio novo, manutenção do veículo).
        <button class="btn btn-claro" id="mot-ativar-push">Ativar avisos</button>`;
      caixa.appendChild(convite);
      convite.querySelector('#mot-ativar-push')!.addEventListener('click', () => {
        void ativarPush().then((r) => {
          if (r.estado === 'ativado') {
            convite.remove();
            avisar('Avisos ativados — mandando um teste…');
            void api.post<{ entregues: number }>('/api/push/teste', {}).then((t) => {
              if (t.entregues === 0) avisar('O teste não chegou — tente de novo.', 'atencao');
            });
          } else {
            // O motivo real, não "tente de novo": quase sempre é o iPhone sem
            // o app na tela de início, ou a permissão bloqueada.
            avisarBloqueante(r.detalhe);
          }
        });
      });
    });
  }

  raiz.querySelector('#filtros')!.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>('[data-f]');
    if (!b) return;
    aba = b.dataset['f'] as typeof aba;
    raiz.querySelectorAll<HTMLElement>('[data-f]').forEach((x) => {
      x.setAttribute('aria-pressed', String(x.dataset['f'] === aba));
    });
    void carregar();
  });

  /**
   * ⛽ REGISTRAR ABASTECIMENTO — o fluxo do desenho do Lucas (05/08):
   * foto do recibo → a IA preenche → o motorista CONFERE → digita o km
   * (teclado numérico, só aceita maior que o último) → o consumo do trecho
   * aparece na hora.
   */
  async function abastecer(): Promise<void> {
    aberto = null;
    lista.innerHTML = `<p class="carregando">Carregando seu veículo…</p>`;
    // SÓ os veículos vinculados a mim no cadastro da frota (11/08, Lucas):
    // o motorista não escolhe entre os carros dos outros.
    let veiculos: { placa: string; descricao: string | null; km_atual: number; motorista_id: number | null }[];
    try {
      veiculos = (await api.get<{ veiculos: typeof veiculos }>('/api/motorista/veiculos')).veiculos;
    } catch {
      lista.innerHTML = `<p class="aviso">Não consegui carregar seu veículo.</p>`;
      return;
    }
    if (veiculos.length === 0) {
      lista.innerHTML = `<div class="vazio"><p>Nenhum veículo vinculado a você — peça ao administrador para vincular na Administração ▸ Frota.</p></div>`;
      return;
    }

    lista.innerHTML = `
      <article class="mot-ficha">
        <h2>⛽ Registrar abastecimento</h2>
        ${
          veiculos.length === 1
            ? `<p class="ab-meu-veiculo">🚛 <b>${escapar(veiculos[0]!.placa)}</b>${
                veiculos[0]!.descricao ? ` — ${escapar(veiculos[0]!.descricao)}` : ''
              }<input type="hidden" id="ab-veiculo" value="${escapar(veiculos[0]!.placa)}"></p>`
            : `<label class="rotulado"><span>Veículo (os seus)</span>
                 <select id="ab-veiculo">${veiculos
                   .map(
                     (v) =>
                       `<option value="${escapar(v.placa)}">${escapar(v.placa)}${
                         v.descricao ? ` — ${escapar(v.descricao)}` : ''
                       }</option>`,
                   )
                   .join('')}</select></label>`
        }

        <div id="ab-painel"><p class="carregando">Carregando o painel do veículo…</p></div>

        <h3>Registrar abastecimento</h3>
        <p class="ab-rascunho" id="ab-rascunho" hidden></p>

        <div class="ab-comprovante">
          <span class="ab-comprovante-titulo">1️⃣ Requisição <i class="sub-inline">o talão do posto com o número em vermelho</i></span>
          <div class="ab-foto-botoes">
            <button class="btn" type="button" id="ab-foto-camera">📷 Tirar foto</button>
            <button class="btn btn-claro" type="button" id="ab-foto-galeria">🖼️ Enviar da galeria</button>
            <button class="btn btn-claro" type="button" id="ab-foto-reenviar" hidden>🔁 Reenviar</button>
            <button class="btn btn-claro ab-sem-req" type="button" id="ab-sem-req" aria-pressed="false">⛽ Posto não cadastrado (sem requisição)</button>
            <span class="ab-foto-ok" id="ab-foto-ok" hidden>✓ requisição enviada</span>
          </div>
          <p class="dica ab-sem-req-aviso" id="ab-sem-req-aviso" hidden>Sem requisição: tire a foto da nota fiscal e informe o <b>centro de custo</b> (obra ou setor) abaixo — se faltar algum, o registro vai para a revisão da Administração.</p>
          <input type="file" id="ab-foto" accept="image/*" capture="environment" hidden>
          <input type="file" id="ab-foto-rolo" accept="image/*" hidden>
          <p class="dica" id="ab-lendo" hidden>🤖 Lendo a requisição…</p>
          <p class="ped-alerta" id="ab-ticket-usado" hidden></p>
          <label class="rotulado"><span>Nº da requisição (o em vermelho) — único, não repete</span>
            <input id="ab-ticket" maxlength="40" inputmode="numeric" placeholder="lido da foto; confira"></label>
          <div class="ab-escolha" id="ab-ticket-escolha" hidden></div>
        </div>

        <div class="ab-comprovante">
          <span class="ab-comprovante-titulo">2️⃣ Nota fiscal <i class="sub-inline">o cupom com QR code</i></span>
          <div class="ab-foto-botoes">
            <button class="btn" type="button" id="ab-nf-camera">📷 Tirar foto</button>
            <button class="btn btn-claro" type="button" id="ab-nf-galeria">🖼️ Enviar da galeria</button>
            <button class="btn btn-claro" type="button" id="ab-nf-reenviar" hidden>🔁 Reenviar</button>
            <span class="ab-foto-ok" id="ab-nf-ok" hidden>✓ nota enviada</span>
          </div>
          <input type="file" id="ab-nf-foto" accept="image/*" capture="environment" hidden>
          <input type="file" id="ab-nf-rolo" accept="image/*" hidden>
          <p class="dica" id="ab-nf-lendo" hidden>🤖 Lendo a nota fiscal…</p>
          <p class="ped-alerta" id="ab-nf-usada" hidden></p>
          <p class="dica" id="ab-nf-info" hidden></p>
          <p class="ped-alerta" id="ab-fonte-aviso" hidden></p>
          <div class="ped-alerta ab-divergencias" id="ab-divergencias" hidden></div>
        </div>

        <div class="form-2col">
          <label class="rotulado"><span>Litros</span>
            <input id="ab-litros" type="number" min="0.01" step="any" inputmode="decimal"></label>
          <label class="rotulado"><span>Preço por litro (R$)</span>
            <input id="ab-preco" type="number" min="0" step="0.001" inputmode="decimal"></label>
          <label class="rotulado"><span>Total (R$)</span>
            <input id="ab-total" type="number" min="0" step="0.01" inputmode="decimal"></label>
          <label class="rotulado"><span>Combustível</span>
            <input id="ab-comb" maxlength="60" placeholder="Diesel S-10"></label>
          <label class="rotulado"><span>Posto</span>
            <input id="ab-posto" maxlength="200"></label>
          <label class="rotulado"><span>Nº da nota</span>
            <input id="ab-nf" maxlength="60"></label>
          <label class="rotulado"><span>Centro de custo (obra ou setor) — lido da requisição; confira</span>
            <input id="ab-cc" list="ab-cc-lista" maxlength="120" placeholder="ADM, Rancho, DMI ou a obra">
            <datalist id="ab-cc-lista"></datalist></label>
        </div>

        <div class="ab-km-ia" id="ab-km-ia" hidden></div>

        <label class="rotulado mot-km">
          <span>Quilometragem do veículo AGORA (obrigatória) — última: <b id="ab-ultimo">…</b></span>
          <input id="ab-km" type="number" inputmode="numeric" pattern="[0-9]*" min="1">
          <span class="ped-alerta" id="ab-km-erro" hidden></span>
        </label>

        <div class="conf-acoes">
          <button class="btn btn-grande" id="ab-salvar">✓ Registrar abastecimento</button>
          <button class="btn btn-claro" id="ab-cancelar">Cancelar</button>
        </div>
      </article>`;

    // Com um veículo só, `#ab-veiculo` é um input escondido — sem seletor.
    const sel = lista.querySelector<HTMLSelectElement | HTMLInputElement>('#ab-veiculo')!;
    const kmCampo = lista.querySelector<HTMLInputElement>('#ab-km')!;
    const kmErro = lista.querySelector<HTMLElement>('#ab-km-erro')!;
    const ultimoEl = lista.querySelector<HTMLElement>('#ab-ultimo')!;
    let ultimoKm = 0;
    let reciboCaminho: string | undefined;
    let lidoPorIa: Record<string, unknown> | undefined;
    let nfCaminho: string | undefined;
    let nfLido: Record<string, unknown> | undefined;
    /** A km que a IA leu na foto — a base do flag de correção manual. */
    let kmLidoIa: number | undefined;

    // TRAVA (12/09): em produção o motorista apertava "Registrar" enquanto a
    // requisição ainda estava sendo lida (uns 6 a 17 s) e recebia "falta a
    // foto". Enquanto há leitura em andamento o botão espera.
    let lendoAgora = 0;
    const salvarBtn = lista.querySelector<HTMLButtonElement>('#ab-salvar')!;
    const marcarLendo = (delta: number): void => {
      lendoAgora = Math.max(0, lendoAgora + delta);
      salvarBtn.disabled = lendoAgora > 0;
      salvarBtn.textContent = lendoAgora > 0 ? '⏳ Lendo o comprovante…' : '✓ Registrar abastecimento';
    };
    /** O último arquivo escolhido de cada comprovante — para reenviar se a rede falhar. */
    const arquivos: { ticket?: File; nota?: File } = {};

    // POSTO NÃO CADASTRADO (16/09, Lucas): não existe o talão de requisição —
    // o motorista paga, pega o cupom fiscal e registra só com a nota. O
    // centro de custo, que viria escrito na requisição, passa a ser digitado.
    let semRequisicao = false;
    const semReqBtn = lista.querySelector<HTMLButtonElement>('#ab-sem-req')!;
    const aplicarSemRequisicao = (ligado: boolean): void => {
      semRequisicao = ligado;
      semReqBtn.setAttribute('aria-pressed', String(ligado));
      semReqBtn.textContent = ligado ? '↩️ Tem requisição, afinal' : '⛽ Posto não cadastrado (sem requisição)';
      lista.querySelector<HTMLElement>('#ab-sem-req-aviso')!.hidden = !ligado;
      for (const id of ['#ab-foto-camera', '#ab-foto-galeria', '#ab-ticket']) {
        const el = lista.querySelector<HTMLButtonElement | HTMLInputElement>(id)!;
        el.disabled = ligado;
      }
      lista.querySelector<HTMLElement>('.ab-comprovante')!.classList.toggle('ab-comprovante-dispensado', ligado);
    };
    semReqBtn.addEventListener('click', () => {
      aplicarSemRequisicao(!semRequisicao);
      salvarRascunho();
      if (semRequisicao) lista.querySelector<HTMLInputElement>('#ab-cc')!.focus();
    });

    // A lista de obras e setores para o campo de centro de custo.
    void api
      .get<{ obras: { codigo: string; nome: string }[] }>('/api/obras?situacao=ativas&ordem=nome')
      .then((r) => {
        const dl = lista.querySelector<HTMLDataListElement>('#ab-cc-lista');
        if (!dl) return;
        const fixos = ['ADM', 'Administração', 'Rancho', 'DMI'];
        dl.innerHTML =
          fixos.map((f) => `<option value="${escapar(f)}"></option>`).join('') +
          (r.obras ?? []).map((o) => `<option value="${escapar(`${o.codigo} — ${o.nome}`)}"></option>`).join('');
      })
      .catch(() => {});
    /** Preenche o centro de custo quando ainda está vazio (a pessoa pode corrigir). */
    const sugerirCentroCusto = (c: { texto: string } | null | undefined): void => {
      const el = lista.querySelector<HTMLInputElement>('#ab-cc')!;
      if (c?.texto && el.value.trim() === '') el.value = c.texto;
    };
    /**
     * Quem preencheu cada campo de valor (12/09, Lucas: "a nota é mais
     * confiável"): a requisição manuscrita só entra em campo VAZIO; a nota
     * impressa entra em campo vazio ou que ainda tem o valor da requisição —
     * nunca por cima do que o motorista digitou.
     */
    const doTicket: Record<string, string> = {};
    const CAMPOS_VALOR: [string, string][] = [
      ['#ab-litros', 'litros'],
      ['#ab-preco', 'precoLitro'],
      ['#ab-total', 'valorTotal'],
      ['#ab-comb', 'combustivel'],
      ['#ab-posto', 'posto'],
    ];

    // RASCUNHO (12/09): o Android mata a aba ao voltar da câmera e o motorista
    // perdia as fotos já enviadas. O que já foi lido fica no aparelho e volta
    // sozinho ao reabrir o formulário (até 6 horas).
    const CHAVE_RASCUNHO = 'cd-abastecimento-rascunho';
    const CAMPOS = ['#ab-litros', '#ab-preco', '#ab-total', '#ab-comb', '#ab-posto', '#ab-nf', '#ab-ticket', '#ab-km', '#ab-cc'];
    interface Rascunho {
      placa: string;
      em: string;
      reciboCaminho?: string;
      lidoPorIa?: Record<string, unknown>;
      nfCaminho?: string;
      nfLido?: Record<string, unknown>;
      kmLidoIa?: number;
      semRequisicao?: boolean;
      campos: Record<string, string>;
    }
    const limparRascunho = (): void => {
      try {
        localStorage.removeItem(CHAVE_RASCUNHO);
      } catch {
        /* sem armazenamento */
      }
    };
    const salvarRascunho = (): void => {
      try {
        const campos: Record<string, string> = {};
        for (const id of CAMPOS) campos[id] = lista.querySelector<HTMLInputElement>(id)?.value ?? '';
        if (!reciboCaminho && !nfCaminho && Object.values(campos).every((v) => v === '')) return limparRascunho();
        const r: Rascunho = {
          placa: sel.value,
          em: new Date().toISOString(),
          campos,
          ...(reciboCaminho ? { reciboCaminho } : {}),
          ...(lidoPorIa ? { lidoPorIa } : {}),
          ...(nfCaminho ? { nfCaminho } : {}),
          ...(nfLido ? { nfLido } : {}),
          ...(kmLidoIa !== undefined ? { kmLidoIa } : {}),
          ...(semRequisicao ? { semRequisicao: true } : {}),
        };
        localStorage.setItem(CHAVE_RASCUNHO, JSON.stringify(r));
      } catch {
        /* sem armazenamento — segue sem rascunho */
      }
    };
    const lerRascunho = (): Rascunho | null => {
      try {
        const bruto = localStorage.getItem(CHAVE_RASCUNHO);
        if (!bruto) return null;
        const r = JSON.parse(bruto) as Rascunho;
        if (!r.placa || !r.campos || Date.now() - new Date(r.em).getTime() > 6 * 3600_000) {
          limparRascunho();
          return null;
        }
        return r;
      } catch {
        return null;
      }
    };

    /**
     * As DUAS FONTES (08/09, Lucas): ticket e nota fiscal trazem km, número
     * do ticket, placa e motorista. Quando batem, entra sozinho; quando
     * divergem, o motorista ESCOLHE; placa/motorista diferentes do que está
     * sendo lançado viram aviso na hora (e inconsistência no envio).
     */
    type Fonte = { km?: number | undefined; ticket?: string | undefined; placa?: string | undefined; motorista?: string | undefined };
    const fontes: { ticket: Fonte; nota: Fonte } = { ticket: {}, nota: {} };
    const lerFonte = (lido: Record<string, unknown>): Fonte => {
      const km = Number(lido['km']);
      const texto = (k: string) => (typeof lido[k] === 'string' && String(lido[k]).trim() ? String(lido[k]).trim() : undefined);
      return {
        km: Number.isInteger(km) && km > 0 ? km : undefined,
        ticket: texto('ticketNumero'),
        placa: texto('placa'),
        motorista: texto('motorista'),
      };
    };

    /**
     * VÍRGULA ESQUECIDA (14/09, Lucas): o hodômetro mostra 280957,5 e a
     * pessoa digita "2809575". Se a km ÷ 10 cai logo acima da última
     * registrada e a inteira não, é a vírgula — a tela entende e avisa. A
     * mesma regra roda no servidor.
     */
    const kmSemVirgula = (km: number, referencia: number): number | null => {
      if (!(referencia > 0) || !Number.isFinite(km)) return null;
      const candidato = Math.floor(km / 10);
      if (km - referencia <= 1000) return null;
      if (candidato <= referencia || candidato - referencia > 5000) return null;
      return candidato;
    };

    // Antes da primeira chamada de `painelVeiculo` — declarar depois deixava a
    // variável na zona morta e o primeiro desenho do painel morria no catch.
    let agrupamento: 'semana' | 'mes' | 'ano' = 'mes';

    const atualizarUltimo = () => {
      const v = veiculos.find((x) => x.placa === sel.value);
      ultimoKm = v?.km_atual ?? 0;
      ultimoEl.textContent = ultimoKm.toLocaleString('pt-BR') + ' km';
      kmCampo.min = String(ultimoKm + 1);
      void painelVeiculo(sel.value);
    };
    sel.addEventListener('change', atualizarUltimo);
    atualizarUltimo();

    /**
     * O PAINEL do veículo (10/08, Lucas): consumo médio geral, consumo do
     * último abastecimento, km atual, o gráfico de km rodados por período
     * (semanas/meses/anos) e o histórico completo dos abastecimentos.
     */
    async function painelVeiculo(placa: string): Promise<void> {
      const caixa = lista.querySelector<HTMLElement>('#ab-painel');
      if (!caixa) return;
      try {
        const [h, serie] = await Promise.all([
          api.get<{
            veiculo: { km_atual: number };
            abastecimentos: {
              ocorrido_em: string; litros: number; valor_total: number | null;
              preco_litro: number | null; combustivel: string | null; posto: string | null;
              km: number; recibo_foto: string | null; consumo_km_l: number | null;
            }[];
          }>(`/api/frota/veiculos/${encodeURIComponent(placa)}/historico`),
          api.get<{ periodos: { periodo: string; km: number }[] }>(
            `/api/frota/veiculos/${encodeURIComponent(placa)}/km-rodados?agrupamento=${agrupamento}`,
          ),
        ]);
        const abs = h.abastecimentos;
        const comConsumo = abs.filter((a) => a.consumo_km_l !== null);
        const media = comConsumo.length
          ? comConsumo.reduce((s3, a) => s3 + (a.consumo_km_l ?? 0), 0) / comConsumo.length
          : null;
        const ultimo = comConsumo[0]?.consumo_km_l ?? null;
        const teto = Math.max(...serie.periodos.map((x) => x.km), 1);
        const rotuloP = (iso: string) =>
          agrupamento === 'ano'
            ? iso.slice(0, 4)
            : agrupamento === 'mes'
              ? `${iso.slice(5, 7)}/${iso.slice(2, 4)}`
              : `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;

        caixa.innerHTML = `
          <div class="pn-kpis">
            <div class="pn-kpi"><b>${media !== null ? media.toFixed(1).replace('.', ',') : '—'}</b><span>km/l — média geral</span></div>
            <div class="pn-kpi"><b>${ultimo !== null ? ultimo.toFixed(1).replace('.', ',') : '—'}</b><span>km/l — último abastecimento</span></div>
            <div class="pn-kpi"><b>${h.veiculo.km_atual.toLocaleString('pt-BR')}</b><span>km atual no sistema</span></div>
          </div>

          <div class="pn-cab"><h3>Km rodados</h3>
            <div class="chips" id="ab-agrup">
              ${(['semana', 'mes', 'ano'] as const)
                .map(
                  (g) =>
                    `<button class="chip" data-g="${g}" aria-pressed="${g === agrupamento}">${
                      g === 'semana' ? 'Semanas' : g === 'mes' ? 'Meses' : 'Anos'
                    }</button>`,
                )
                .join('')}
            </div>
          </div>
          ${
            serie.periodos.length === 0
              ? `<p class="dica">O gráfico nasce a partir do 2º abastecimento com km.</p>`
              : `<div class="ab-grafico">${serie.periodos
                  .slice(-24)
                  .map(
                    (x) => `<div class="ab-col" title="${x.km.toLocaleString('pt-BR')} km">
                      <i style="height:${Math.max((x.km / teto) * 100, 3).toFixed(1)}%"></i>
                      <span class="ab-valor">${x.km.toLocaleString('pt-BR')}</span>
                      <span class="ab-rotulo">${rotuloP(x.periodo)}</span>
                    </div>`,
                  )
                  .join('')}</div>`
          }

          <h3>Histórico de abastecimentos <span class="sub-inline">${abs.length}</span></h3>
          ${
            abs.length === 0
              ? `<p class="dica">Nenhum abastecimento registrado ainda.</p>`
              : abs
                  .map(
                    (a) => `<div class="ab-hist">
                    ${
                      a.recibo_foto
                        ? `<img src="${escapar(api.foto(a.recibo_foto) ?? '')}" data-grande="${escapar(api.foto(a.recibo_foto) ?? '')}" alt="">`
                        : '<span class="sem">—</span>'
                    }
                    <span>
                      <b>${dataHora(a.ocorrido_em)}</b> · ${numero(a.litros)} L${
                        a.preco_litro !== null ? ` × R$ ${a.preco_litro.toFixed(2).replace('.', ',')}` : ''
                      }${a.valor_total !== null ? ` = R$ ${a.valor_total.toFixed(2).replace('.', ',')}` : ''}
                      <span class="sub-inline">${a.km.toLocaleString('pt-BR')} km${
                        a.consumo_km_l !== null ? ` · <b>${a.consumo_km_l.toFixed(1).replace('.', ',')} km/l</b>` : ''
                      }${a.posto ? ` · ${escapar(a.posto)}` : ''}${a.combustivel ? ` · ${escapar(a.combustivel)}` : ''}</span>
                    </span>
                  </div>`,
                  )
                  .join('')
          }`;

        caixa.querySelector('#ab-agrup')?.addEventListener('click', (e) => {
          const b2 = (e.target as HTMLElement).closest<HTMLElement>('[data-g]');
          if (!b2) return;
          agrupamento = b2.dataset['g'] as typeof agrupamento;
          void painelVeiculo(sel.value);
        });
      } catch (erro) {
        console.warn('painel do veículo falhou', erro);
        caixa.innerHTML = `<p class="dica">Não consegui carregar o painel do veículo.</p>`;
      }
    }

    // Só aceita número MAIOR que o último — avisa enquanto digita. E salto
    // acima de 1.000 km (10/09, Lucas) é improvável: quase sempre um dígito
    // a mais, o décimo do hodômetro copiado sem a vírgula. Aqui só avisa;
    // no envio o servidor devolve como inconsistência.
    kmCampo.addEventListener('input', () => {
      const v = Number(kmCampo.value);
      const entendida = kmCampo.value ? kmSemVirgula(v, ultimoKm) : null;
      if (entendida !== null) {
        // "2809575" → 280.957: troca no campo e diz o que fez. Quem digitou
        // confere; se não era isso, corrige por cima.
        kmCampo.value = String(entendida);
        kmErro.textContent = `✂️ Entendi ${v.toLocaleString('pt-BR')} como ${entendida.toLocaleString('pt-BR')} km — a vírgula do hodômetro ficou de fora. Confira.`;
        kmErro.hidden = false;
        return;
      }
      if (kmCampo.value && v <= ultimoKm) {
        kmErro.textContent = `precisa ser maior que ${ultimoKm.toLocaleString('pt-BR')}`;
        kmErro.hidden = false;
      } else if (kmCampo.value && ultimoKm > 0 && v - ultimoKm > 1000) {
        kmErro.textContent = `⚠️ ${(v - ultimoKm).toLocaleString('pt-BR')} km desde o último abastecimento — mais de 1.000 km é improvável. Confira se não entrou um dígito a mais (o décimo do hodômetro, depois da vírgula).`;
        kmErro.hidden = false;
      } else {
        kmErro.hidden = true;
      }
    });

    // Os DOIS jeitos de mandar cada comprovante (18/08, Lucas): "tirar foto"
    // abre a câmera (capture) e "da galeria" abre o rolo — eram um botão só,
    // escrito "enviar arquivo", que só abria a câmera.
    lista.querySelector('#ab-foto-camera')!.addEventListener('click', () =>
      lista.querySelector<HTMLInputElement>('#ab-foto')!.click());
    lista.querySelector('#ab-foto-galeria')!.addEventListener('click', () =>
      lista.querySelector<HTMLInputElement>('#ab-foto-rolo')!.click());
    lista.querySelector('#ab-nf-camera')!.addEventListener('click', () =>
      lista.querySelector<HTMLInputElement>('#ab-nf-foto')!.click());
    lista.querySelector('#ab-nf-galeria')!.addEventListener('click', () =>
      lista.querySelector<HTMLInputElement>('#ab-nf-rolo')!.click());

    const enviarTicket = (arq: File): void => {
      arquivos.ticket = arq;
      const lendo = lista.querySelector<HTMLElement>('#ab-lendo')!;
      lendo.hidden = false;
      lista.querySelector<HTMLElement>('#ab-foto-reenviar')!.hidden = true;
      marcarLendo(+1);
      const corpo = new FormData();
      corpo.append('arquivo', arq);
      void api
        .enviarMultipart<{
          caminho: string;
          lido: Record<string, unknown> & { erro?: string };
          ticketJaUsado: string | null;
          centroCusto?: { texto: string } | null;
        }>('/api/frota/recibo', corpo)
        .then((r) => {
          marcarLendo(-1);
          lendo.hidden = true;
          reciboCaminho = r.caminho;
          lidoPorIa = r.lido;
          lista.querySelector<HTMLElement>('#ab-foto-ok')!.hidden = false;
          const usadoEl = lista.querySelector<HTMLElement>('#ab-ticket-usado')!;
          if (r.ticketJaUsado) {
            usadoEl.textContent = `⚠️ ${r.ticketJaUsado} Confira o número — se a leitura errou, corrija no campo abaixo; se estiver certo, o envio vai para a revisão da Administração.`;
            usadoEl.hidden = false;
          } else {
            usadoEl.hidden = true;
          }
          if (r.lido.erro) {
            salvarRascunho();
            avisarBloqueante(`${String(r.lido.erro)}\n\nA foto ficou guardada — preencha os campos à mão e registre.`);
            return;
          }
          for (const [id, chave] of CAMPOS_VALOR) {
            const v = r.lido[chave];
            const el = lista.querySelector<HTMLInputElement>(id)!;
            if (v === undefined || v === null || v === '') continue;
            doTicket[id] = String(v);
            if (el.value === '') el.value = String(v);
          }
          // A KM e o Nº do ticket NÃO entram sozinhos no campo: passam pela
          // reconciliação com a nota fiscal (a outra fonte) — confirmar,
          // escolher entre as duas ou corrigir (11/08 e 08/09, Lucas).
          fontes.ticket = lerFonte(r.lido);
          sugerirCentroCusto(r.centroCusto);
          reconciliar();
          salvarRascunho();
          avisar('Requisição lida — CONFIRA os valores antes de salvar.');
        })
        .catch((e) => {
          marcarLendo(-1);
          lendo.hidden = true;
          lista.querySelector<HTMLElement>('#ab-foto-reenviar')!.hidden = false;
          avisarBloqueante(
            `Não consegui ENVIAR a foto da requisição${
              e instanceof ErroApi ? ` (${e.message})` : ' — a rede pode ter caído'
            }. Toque em 🔁 Reenviar.`,
          );
        });
    };
    const aoEscolherTicket = (e: Event): void => {
      const arq = (e.target as HTMLInputElement).files?.[0];
      if (arq) enviarTicket(arq);
    };
    lista.querySelector('#ab-foto')!.addEventListener('change', aoEscolherTicket);
    lista.querySelector('#ab-foto-rolo')!.addEventListener('change', aoEscolherTicket);
    lista.querySelector('#ab-foto-reenviar')!.addEventListener('click', () => {
      if (arquivos.ticket) enviarTicket(arquivos.ticket);
    });

    /** Mostra as divergências ticket × nota (as mesmas que o servidor aponta). */
    const mostrarDivergencias = (mensagens: string[]): void => {
      const caixa = lista.querySelector<HTMLElement>('#ab-divergencias')!;
      if (mensagens.length === 0) {
        caixa.hidden = true;
        caixa.innerHTML = '';
        return;
      }
      // O motorista corrige o que der; se o papel está assim mesmo, "enviar
      // mesmo assim" — o registro entra marcado e cai na revisão da
      // Administração (08/09, Lucas).
      caixa.innerHTML =
        `<b>⚠️ Encontrei inconsistências:</b><ul>${mensagens
          .map((m) => `<li>${escapar(m)}</li>`)
          .join('')}</ul>` +
        `<span>Corrija o que estiver errado (refaça ou reenvie a foto) e tente de novo — ou envie assim mesmo para a Administração revisar.</span>` +
        `<button class="btn btn-claro" type="button" id="ab-revisao">📨 Enviar mesmo assim (vai para revisão)</button>`;
      caixa.hidden = false;
      caixa.querySelector('#ab-revisao')?.addEventListener('click', () => {
        void salvar(true);
      });
    };

    const enviarNota = (arq: File): void => {
      arquivos.nota = arq;
      const lendo = lista.querySelector<HTMLElement>('#ab-nf-lendo')!;
      lendo.hidden = false;
      lista.querySelector<HTMLElement>('#ab-nf-reenviar')!.hidden = true;
      marcarLendo(+1);
      const corpo = new FormData();
      corpo.append('arquivo', arq);
      void api
        .enviarMultipart<{
          caminho: string;
          lido: Record<string, unknown> & { erro?: string };
          nfJaUsada: string | null;
          centroCusto?: { texto: string } | null;
        }>('/api/frota/nota-fiscal', corpo)
        .then((r) => {
          marcarLendo(-1);
          lendo.hidden = true;
          nfCaminho = r.caminho;
          nfLido = r.lido;
          mostrarDivergencias([]);
          lista.querySelector<HTMLElement>('#ab-nf-ok')!.hidden = false;
          const usadaEl = lista.querySelector<HTMLElement>('#ab-nf-usada')!;
          if (r.nfJaUsada) {
            usadaEl.textContent = `🚫 ${r.nfJaUsada} Esta nota não pode pagar dois abastecimentos.`;
            usadaEl.hidden = false;
          } else {
            usadaEl.hidden = true;
          }
          if (r.lido.erro) {
            salvarRascunho();
            avisarBloqueante(`${String(r.lido.erro)}\n\nA foto ficou guardada — confira os campos e registre.`);
            return;
          }
          const info = lista.querySelector<HTMLElement>('#ab-nf-info')!;
          const partes: string[] = [];
          if (typeof r.lido['emitidaEm'] === 'string') {
            partes.push(`emitida em ${new Date(String(r.lido['emitidaEm'])).toLocaleString('pt-BR')}`);
          }
          if (typeof r.lido['chave'] === 'string') {
            partes.push(`chave ${String(r.lido['chave']).replace(/(\d{4})/g, '$1 ').trim()}`);
          }
          if (typeof r.lido['valorTotal'] === 'number') {
            partes.push(`total R$ ${Number(r.lido['valorTotal']).toFixed(2).replace('.', ',')}`);
          }
          fontes.nota = lerFonte(r.lido);
          sugerirCentroCusto(r.centroCusto);
          // A nota impressa também traz os valores — e manda sobre a requisição
          // manuscrita (só não passa por cima do que foi digitado à mão).
          for (const [id, chave] of CAMPOS_VALOR) {
            const v = r.lido[chave === 'combustivel' ? 'produto' : chave];
            const el = lista.querySelector<HTMLInputElement>(id)!;
            if (v === undefined || v === null || v === '') continue;
            if (el.value === '' || el.value === doTicket[id]) el.value = String(v);
          }
          // O Nº da nota é o "NFC-e nº" da própria nota (o da requisição é outro papel).
          if (typeof r.lido['nfNumero'] === 'string' && r.lido['nfNumero']) {
            lista.querySelector<HTMLInputElement>('#ab-nf')!.value = String(r.lido['nfNumero']);
          }
          if (fontes.nota.placa) partes.push(`placa ${fontes.nota.placa}`);
          if (fontes.nota.km) partes.push(`km ${fontes.nota.km.toLocaleString('pt-BR')}`);
          if (fontes.nota.motorista) partes.push(`motorista ${fontes.nota.motorista}`);
          if (fontes.nota.ticket) partes.push(`requisição ${fontes.nota.ticket}`);
          info.textContent = partes.length ? `📄 Nota lida: ${partes.join(' · ')}` : '';
          info.hidden = partes.length === 0;
          reconciliar();
          salvarRascunho();
          avisar('Nota fiscal lida — o sistema confere com a requisição ao salvar.');
        })
        .catch((e) => {
          marcarLendo(-1);
          lendo.hidden = true;
          lista.querySelector<HTMLElement>('#ab-nf-reenviar')!.hidden = false;
          avisarBloqueante(
            `Não consegui ENVIAR a foto da nota${
              e instanceof ErroApi ? ` (${e.message})` : ' — a rede pode ter caído'
            }. Toque em 🔁 Reenviar.`,
          );
        });
    };
    const aoEscolherNf = (e: Event): void => {
      const arq = (e.target as HTMLInputElement).files?.[0];
      if (arq) enviarNota(arq);
    };
    lista.querySelector('#ab-nf-reenviar')!.addEventListener('click', () => {
      if (arquivos.nota) enviarNota(arquivos.nota);
    });
    lista.querySelector('#ab-nf-foto')!.addEventListener('change', aoEscolherNf);
    lista.querySelector('#ab-nf-rolo')!.addEventListener('change', aoEscolherNf);

    /** Junta ticket × nota: preenche o que bate, pede escolha no que diverge. */
    function reconciliar(): void {
      // Nº do ticket
      const t = fontes.ticket.ticket;
      const n = fontes.nota.ticket;
      const campoTicket = lista.querySelector<HTMLInputElement>('#ab-ticket')!;
      const escolhaTicket = lista.querySelector<HTMLElement>('#ab-ticket-escolha')!;
      if (t && n && t !== n) {
        escolhaTicket.hidden = false;
        escolhaTicket.innerHTML = `<span>⚠️ O número da requisição veio diferente nas duas fontes — qual vale?</span>
          <button type="button" class="btn" data-ticket="${escapar(n)}">Da nota fiscal (impressa): ${escapar(n)}</button>
          <button type="button" class="btn btn-claro" data-ticket="${escapar(t)}">Da requisição (manuscrita): ${escapar(t)}</button>`;
        escolhaTicket.querySelectorAll<HTMLElement>('[data-ticket]').forEach((b) =>
          b.addEventListener('click', () => {
            campoTicket.value = b.dataset['ticket']!;
            escolhaTicket.hidden = true;
          }),
        );
      } else {
        escolhaTicket.hidden = true;
        const unico = n ?? t;
        if (unico && (!campoTicket.value || campoTicket.value === t || campoTicket.value === n)) {
          campoTicket.value = unico;
        }
      }

      // Km: a NOTA FISCAL manda (12/09, Lucas — a impressa é mais confiável
      // que a manuscrita, que a IA leu como "7" numa foto real). Km da nota
      // entra sozinha no campo; a da requisição fica como alternativa. Só
      // requisição = confirmar. Sempre pode corrigir. As duas passam pela
      // regra da vírgula esquecida (14/09).
      const entender = (k: number | undefined) => (k ? (kmSemVirgula(k, ultimoKm) ?? k) : k);
      const kt = entender(fontes.ticket.km);
      const kn = entender(fontes.nota.km);
      const faixa = lista.querySelector<HTMLElement>('#ab-km-ia')!;
      const opcoes = [
        ...(kn ? [{ rotulo: 'da nota fiscal (impressa)', valor: kn }] : []),
        ...(kt && kt !== kn ? [{ rotulo: 'da requisição (manuscrita)', valor: kt }] : []),
      ];
      if (opcoes.length === 0) {
        faixa.hidden = true;
        kmLidoIa = undefined;
      } else {
        faixa.hidden = false;
        kmLidoIa = opcoes[0]!.valor;
        faixa.innerHTML =
          `<span>📷 ${kn ? 'Km da nota fiscal já preenchida' : 'Km lida da requisição — confirme'}${
            opcoes.length > 1 ? ' (a requisição diz outra)' : ''
          }:</span>` +
          opcoes
            .map(
              (o) =>
                `<button class="btn${o.valor === kn ? '' : ' btn-claro'}" type="button" data-km="${o.valor}">✓ ${o.valor.toLocaleString('pt-BR')} km (${o.rotulo})${
                  ultimoKm > 0 && o.valor - ultimoKm > 1000 ? ' ⚠️ salto grande' : ''
                }</button>`,
            )
            .join('') +
          `<button class="btn btn-claro" type="button" id="ab-km-corrigir">✏️ Corrigir</button>`;
        if (kn && (kmCampo.value === '' || Number(kmCampo.value) === kt || Number(kmCampo.value) === kn)) {
          kmCampo.value = String(kn);
          kmCampo.dispatchEvent(new Event('input'));
        }
        faixa.querySelectorAll<HTMLElement>('[data-km]').forEach((b) =>
          b.addEventListener('click', () => {
            kmLidoIa = Number(b.dataset['km']);
            kmCampo.value = String(kmLidoIa);
            kmCampo.dispatchEvent(new Event('input'));
            avisar(`Km confirmada: ${kmLidoIa.toLocaleString('pt-BR')}.`);
          }),
        );
        faixa.querySelector('#ab-km-corrigir')!.addEventListener('click', () => {
          kmCampo.value = '';
          kmCampo.focus();
          avisar('Digite a km certa — o registro fica marcado como corrigido à mão.', 'atencao');
        });
      }

      // Placa do comprovante × veículo escolhido: aviso na hora.
      const aviso = lista.querySelector<HTMLElement>('#ab-fonte-aviso')!;
      const chapa = (x: string) => x.toUpperCase().replace(/[^A-Z0-9]/g, '');
      const placaFonte = fontes.nota.placa ?? fontes.ticket.placa;
      const msgs: string[] = [];
      if (placaFonte && chapa(placaFonte) !== chapa(sel.value)) {
        msgs.push(`⚠️ O comprovante está na placa ${placaFonte}, mas você está lançando no ${sel.value}.`);
      }
      aviso.textContent = msgs.join(' ');
      aviso.hidden = msgs.length === 0;
    }
    sel.addEventListener('change', () => reconciliar());

    lista.querySelector('#ab-cancelar')!.addEventListener('click', () => {
      limparRascunho();
      void carregar();
    });
    const salvar = (revisaoHumana: boolean): Promise<void> => {
      return (async () => {
        const litros = Number(lista.querySelector<HTMLInputElement>('#ab-litros')!.value);
        const km = Number(kmCampo.value);
        const ticketNumero = lista.querySelector<HTMLInputElement>('#ab-ticket')!.value.trim();
        // Os dois comprovantes (requisição e nota fiscal) são pedidos, mas a
        // falta não tranca mais o envio (29/10, Lucas — a leitura de uma
        // requisição manuscrita travou um motorista): o servidor devolve a
        // falta na lista de inconsistências, e "enviar mesmo assim" manda o
        // registro para a revisão da Administração.
        if (lendoAgora > 0) {
          return avisarBloqueante('Aguarde — ainda estou lendo o comprovante (leva uns 10 segundos). O botão libera sozinho.');
        }
        const centroCusto = lista.querySelector<HTMLInputElement>('#ab-cc')!.value.trim();
        if (!(litros > 0)) return avisarBloqueante('Informe os litros abastecidos.');
        if (!km) return avisarBloqueante('A quilometragem é obrigatória.');
        if (km <= ultimoKm) {
          return avisarBloqueante(
            `A quilometragem precisa ser maior que a última registrada (${ultimoKm.toLocaleString('pt-BR')}).`,
          );
        }
        try {
          const r = await api.post<{
            consumo: number | null;
            kmRodados: number | null;
            emRevisao?: boolean;
            kmAjustado?: { de: number; para: number } | null;
          }>(`/api/frota/veiculos/${encodeURIComponent(sel.value)}/abastecimentos`, {
            litros,
            km,
            valorTotal: Number(lista.querySelector<HTMLInputElement>('#ab-total')!.value) || undefined,
            precoLitro: Number(lista.querySelector<HTMLInputElement>('#ab-preco')!.value) || undefined,
            combustivel: lista.querySelector<HTMLInputElement>('#ab-comb')!.value.trim() || undefined,
            posto: lista.querySelector<HTMLInputElement>('#ab-posto')!.value.trim() || undefined,
            nfNumero: lista.querySelector<HTMLInputElement>('#ab-nf')!.value.trim() || undefined,
            // O flag de correção manual: havia km lida da foto e a salva é outra.
            ...(kmLidoIa !== undefined ? { kmLidoIa, kmCorrigido: km !== kmLidoIa } : {}),
            ...(reciboCaminho ? { reciboFoto: reciboCaminho } : {}),
            ...(lidoPorIa ? { lidoPorIa } : {}),
            ...(ticketNumero ? { ticketNumero } : {}),
            ...(nfCaminho ? { nfFoto: nfCaminho } : {}),
            ...(typeof nfLido?.['chave'] === 'string' ? { nfChave: nfLido['chave'] } : {}),
            ...(typeof nfLido?.['emitidaEm'] === 'string' ? { nfEmitidaEm: nfLido['emitidaEm'] } : {}),
            ...(nfLido ? { nfLidoPorIa: nfLido } : {}),
            ...(revisaoHumana ? { revisaoHumana: true } : {}),
            ...(semRequisicao ? { semRequisicao: true } : {}),
            ...(centroCusto ? { centroCusto } : {}),
          });
          limparRascunho();
          if (r.kmAjustado) {
            avisarBloqueante(
              `A km ${r.kmAjustado.de.toLocaleString('pt-BR')} foi entendida como ${r.kmAjustado.para.toLocaleString('pt-BR')} km — a vírgula do hodômetro tinha ficado de fora. Se não era isso, a Administração corrige na ficha do veículo.`,
            );
          }
          if (r.emRevisao) {
            avisarBloqueante(
              'Abastecimento enviado COM INCONSISTÊNCIA — a Administração vai revisar os comprovantes.',
            );
          }
          // O aviso de consumo, na hora — é a análise que o Lucas pediu.
          if (r.consumo !== null) {
            avisarBloqueante(
              `Abastecimento registrado.\n\nConsumo do trecho: ${r.consumo.toFixed(1).replace('.', ',')} km/l` +
                (r.kmRodados ? ` (${r.kmRodados.toLocaleString('pt-BR')} km rodados desde o último abastecimento).` : '.'),
            );
          } else {
            avisar('Abastecimento registrado — o consumo aparece a partir do próximo.');
          }
          void carregar();
        } catch (e) {
          // Divergência ticket × nota: o servidor recusa com a lista. Duas
          // recusas seguidas liberam o caminho da análise humana.
          if (e instanceof ErroApi && e.status === 409 && e.detalhes?.length) {
            mostrarDivergencias(e.detalhes);
            lista.querySelector<HTMLElement>('#ab-divergencias')!.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
          }
          avisarBloqueante(e instanceof ErroApi ? e.message : 'Não consegui registrar.');
        }
      })();
    };
    lista.querySelector('#ab-salvar')!.addEventListener('click', () => void salvar(false));

    // Cada campo digitado vai para o rascunho (com folga, para não gravar a cada tecla).
    let rascunhoTimer: number | undefined;
    for (const id of CAMPOS) {
      lista.querySelector(id)?.addEventListener('input', () => {
        window.clearTimeout(rascunhoTimer);
        rascunhoTimer = window.setTimeout(salvarRascunho, 400);
      });
    }

    // Volta o rascunho, se houver um recente com foto para um dos meus veículos.
    const rascunho = lerRascunho();
    if (rascunho && veiculos.some((v) => v.placa === rascunho.placa) && (rascunho.reciboCaminho || rascunho.nfCaminho || rascunho.semRequisicao)) {
      if (sel.value !== rascunho.placa) {
        sel.value = rascunho.placa;
        atualizarUltimo();
      }
      reciboCaminho = rascunho.reciboCaminho;
      lidoPorIa = rascunho.lidoPorIa;
      nfCaminho = rascunho.nfCaminho;
      nfLido = rascunho.nfLido;
      kmLidoIa = rascunho.kmLidoIa;
      if (lidoPorIa) fontes.ticket = lerFonte(lidoPorIa);
      if (nfLido) fontes.nota = lerFonte(nfLido);
      for (const [id, v] of Object.entries(rascunho.campos)) {
        const el = lista.querySelector<HTMLInputElement>(id);
        if (el && v) el.value = v;
      }
      lista.querySelector<HTMLElement>('#ab-foto-ok')!.hidden = !reciboCaminho;
      lista.querySelector<HTMLElement>('#ab-nf-ok')!.hidden = !nfCaminho;
      if (rascunho.semRequisicao) aplicarSemRequisicao(true);
      reconciliar();
      kmCampo.dispatchEvent(new Event('input'));
      const hora = new Date(rascunho.em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
      const oQue = [reciboCaminho ? 'requisição' : '', nfCaminho ? 'nota' : ''].filter(Boolean).join(' e ');
      const banner = lista.querySelector<HTMLElement>('#ab-rascunho')!;
      banner.innerHTML = `↩️ Continuando o abastecimento começado às ${hora} — ${oQue} já enviada${
        reciboCaminho && nfCaminho ? 's' : ''
      }. <button class="btn btn-claro" type="button" id="ab-rascunho-zerar">Começar do zero</button>`;
      banner.hidden = false;
      banner.querySelector('#ab-rascunho-zerar')!.addEventListener('click', () => {
        limparRascunho();
        void abastecer();
      });
    }
  }

  async function carregar() {
    aberto = null;
    try {
      // `meus=1`: só o que ESTA pessoa dirigiu. Sem isso, o motorista via a
      // rota de todo mundo e os dele sumiam na lista.
      const r = await api.get<{ romaneios: RomaneioLista[] }>('/api/romaneios?porPagina=50&meus=1');
      const meus = r.romaneios.filter((x) =>
        aba === 'andamento'
          ? x.status === 'aguardando_retirada' || x.status === 'em_rota'
          : x.status === 'entregue' || x.status === 'cancelado',
      );
      desenhar(meus);
    } catch (erro) {
      lista.innerHTML = `<p class="aviso">${escapar(
        erro instanceof Error ? erro.message : 'Não consegui carregar.',
      )}</p>`;
    }
  }

  function desenhar(rs: RomaneioLista[]) {
    if (rs.length === 0) {
      lista.innerHTML = `<div class="vazio"><p>${
        aba === 'andamento' ? 'Nada para retirar ou entregar agora.' : 'Nada finalizado ainda.'
      }</p></div>`;
      return;
    }
    lista.innerHTML = rs
      .map(
        (r) => `
      <button class="mot-cartao" data-abrir="${escapar(r.numero)}">
        <span class="mot-acao ${r.status === 'aguardando_retirada' ? 'a-retirar' : r.status === 'em_rota' ? 'a-entregar' : 'a-pronto'}">
          ${
            r.status === 'aguardando_retirada'
              ? 'RETIRAR NO GALPÃO'
              : r.status === 'em_rota'
                ? 'ENTREGAR NA OBRA'
                : r.status === 'entregue'
                  ? 'ENTREGUE'
                  : 'CANCELADO'
          }
        </span>
        <b class="mot-obra">${escapar(r.obraNome ?? '🛠️ Concerto de ferramentas')}</b>
        <span class="mot-meta">${r.itens} item(ns) · ${escapar(r.numero)}</span>
        ${r.obraEndereco ? `<span class="mot-end">${escapar(r.obraEndereco)}</span>` : ''}
        <span class="mot-quando">${
          r.status === 'entregue' && r.entregue_em
            ? `Entregue ${quando(r.entregue_em)}`
            : r.status === 'em_rota' && r.retirado_em
              ? `Retirado ${quando(r.retirado_em)}`
              : `Criado ${quando(r.criado_em)}`
        }</span>
      </button>`,
      )
      .join('');

    lista.querySelectorAll<HTMLElement>('[data-abrir]').forEach((b) => {
      b.addEventListener('click', () => void abrir(b.dataset['abrir']!));
    });
  }

  async function abrir(numeroRomaneio: string) {
    aberto = numeroRomaneio;
    lista.innerHTML = `<p class="carregando">Abrindo…</p>`;
    try {
      const r = await api.get<RomaneioDetalhe>(
        `/api/romaneios/${encodeURIComponent(numeroRomaneio)}`,
      );
      if (aberto !== numeroRomaneio) return;
      detalhe(r);
    } catch (erro) {
      lista.innerHTML = `<p class="aviso">${escapar(
        erro instanceof ErroApi ? erro.message : 'Não consegui abrir.',
      )}</p>`;
    }
  }

  function detalhe(r: RomaneioDetalhe) {
    const podeRetirar = r.status === 'aguardando_retirada';
    const podeEntregar = r.status === 'em_rota';

    lista.innerHTML = `
      <button class="voltar" id="voltar">← Meus romaneios</button>
      <article class="mot-ficha">
        <h2>${escapar(r.obraNome ?? '🛠️ Concerto de ferramentas')}</h2>
        <p class="mot-num">${escapar(r.numero)}${r.obra ? ` · ${escapar(r.obra)}` : ''}</p>

        ${
          r.finalidade === 'concerto'
            ? `<p class="dica">Leve as ferramentas à loja de concerto e registre abaixo onde deixou cada uma.</p>`
            : r.obraEndereco
              ? `<a class="mot-mapa" target="_blank" rel="noopener"
                  href="https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(r.obraEndereco)}">
                 📍 ${escapar(r.obraEndereco)}
                 <small>tocar para abrir no mapa</small>
               </a>`
              : `<p class="aviso">Esta obra está sem endereço cadastrado.</p>`
        }

        ${r.encarregado ? `<p class="mot-linha"><b>Encarregado:</b> ${escapar(r.encarregado)}</p>` : ''}
        ${r.obs ? `<p class="ficha-obs">${escapar(r.obs)}</p>` : ''}

        ${
          r.finalidade === 'concerto'
            ? `<h3>Ferramentas</h3>
               <div class="mot-ferramentas">
                 ${r.ferramentas
                   .map(
                     (f) => `<label class="mot-fer">
                       <input type="checkbox" data-fer="${f.id}" ${f.empresa_motorista ? 'disabled checked' : ''}>
                       <span><b>${escapar(f.patrimonio)}</b> — ${escapar(f.itemNome)}
                         <span class="sub-inline">${f.defeito ? escapar(f.defeito) : ''}${
                           f.empresa_motorista ? ` · deixada na ${escapar(f.empresa_motorista)}` : ''
                         }</span></span>
                     </label>`,
                   )
                   .join('')}
               </div>
               ${
                 r.ferramentas.some((f) => !f.empresa_motorista) && r.status === 'em_rota'
                   ? `<div class="mot-empresa">
                        <label class="rotulado"><span>Empresa onde deixou as marcadas</span>
                          <input id="mot-empresa" maxlength="200" placeholder="nome da loja de concerto"></label>
                        <button class="btn" id="mot-marcar-empresa">✓ Registrar onde deixei</button>
                      </div>`
                   : ''
               }`
            : `<h3>Carga</h3>
        <ul class="mot-itens">
          ${r.itens
            .map(
              (i) => `<li>
                <span class="mot-qtd">${numero(i.quantidade)}${escapar(i.unidade ? ' ' + i.unidade : '')}</span>
                <span>${escapar(i.nome)}</span>
              </li>`,
            )
            .join('')}
        </ul>`
        }

        ${
          podeRetirar
            ? bloco(
                'retirada',
                'Confirmar retirada',
                'Fotografe a carga no caminhão antes de sair. <b>O estoque baixa agora</b> — a partir daqui o material não está mais disponível para outro pedido.',
                false,
              )
            : ''
        }
        ${
          podeEntregar && r.finalidade !== 'concerto'
            ? bloco(
                'entrega',
                'Confirmar entrega',
                'Fotografe o material entregue e informe quem recebeu.',
                true,
              )
            : ''
        }
        ${
          r.status === 'entregue'
            ? `<div class="mot-pronto">
                 <b>✓ Entregue</b>
                 <span>${r.entregue_em ? dataHora(r.entregue_em) : ''}${
                   r.recebedor ? ` · recebeu: ${escapar(r.recebedor)}` : ''
                 }</span>
               </div>`
            : ''
        }
        ${
          r.status === 'cancelado'
            ? `<div class="mot-pronto cancelado"><b>Cancelado</b></div>`
            : ''
        }
      </article>`;

    lista.querySelector('#voltar')!.addEventListener('click', () => void carregar());
    if (podeRetirar) ligar(r, 'retirada');
    if (podeEntregar && r.finalidade !== 'concerto') ligar(r, 'entrega');

    lista.querySelector('#mot-marcar-empresa')?.addEventListener('click', () => {
      const empresa = lista.querySelector<HTMLInputElement>('#mot-empresa')!.value.trim();
      const ids = [...lista.querySelectorAll<HTMLInputElement>('[data-fer]:checked:not(:disabled)')].map(
        (c) => Number(c.dataset['fer']),
      );
      if (!empresa) return avisarBloqueante('Escreva o nome da empresa.');
      if (!ids.length) return avisarBloqueante('Marque as ferramentas que deixou nessa empresa.');
      void api
        .post('/api/ferramentas/concerto/empresa', { ids, empresa })
        .then(() => {
          avisar(`Registrado: ${ids.length} ferramenta(s) na ${empresa}.`);
          void abrir(r.numero);
        })
        .catch((e) => avisarBloqueante(e instanceof ErroApi ? e.message : 'Não consegui registrar.'));
    });
  }

  /** O bloco de confirmação, igual nos dois momentos exceto pelos campos. */
  function bloco(qual: string, titulo: string, explica: string, pedeRecebedor: boolean): string {
    return `
      <section class="mot-acao-bloco" data-bloco="${qual}">
        <h3>${titulo}</h3>
        <p class="dica">${explica}</p>

        <div class="mot-previa" data-previa></div>
        <label class="btn btn-bloco btn-grande arquivo">
          📷 ${pedeRecebedor ? 'Foto da entrega' : 'Foto da carga'}
          <input type="file" accept="image/*" capture="environment" data-camera ${pedeRecebedor ? 'multiple' : ''} hidden>
        </label>

        ${
          pedeRecebedor
            ? `<label class="rotulado">
                 <span>Quem recebeu</span>
                 <input data-recebedor maxlength="120" placeholder="Nome de quem recebeu na obra" required>
               </label>
               <label class="rotulado">
                 <span>Observação (opcional)</span>
                 <input data-obs maxlength="1000">
               </label>`
            : ''
        }

        <button class="btn btn-bloco btn-grande" data-confirmar>${titulo}</button>
        <p class="aviso" data-erro hidden></p>
        <p class="dica" data-estado></p>
      </section>`;
  }

  function ligar(r: RomaneioDetalhe, qual: 'retirada' | 'entrega') {
    const bl = lista.querySelector<HTMLElement>(`[data-bloco="${qual}"]`)!;
    const camera = bl.querySelector<HTMLInputElement>('[data-camera]')!;
    const previa = bl.querySelector<HTMLElement>('[data-previa]')!;
    const botao = bl.querySelector<HTMLButtonElement>('[data-confirmar]')!;
    const erro = bl.querySelector<HTMLElement>('[data-erro]')!;
    const estado = bl.querySelector<HTMLElement>('[data-estado]')!;
    let escolhidas: File[] = [];

    camera.addEventListener('change', () => {
      escolhidas = Array.from(camera.files ?? []);
      previa.innerHTML = escolhidas
        .map((f) => `<img src="${URL.createObjectURL(f)}" alt="">`)
        .join('');
    });

    botao.addEventListener('click', () => void confirmar());

    async function confirmar() {
      const recebedor = bl.querySelector<HTMLInputElement>('[data-recebedor]')?.value.trim();
      if (qual === 'entrega' && !recebedor) {
        erro.textContent = 'Informe quem recebeu na obra.';
        erro.hidden = false;
        return;
      }
      botao.disabled = true;
      erro.hidden = true;

      try {
        estado.textContent = 'Enviando foto…';
        const caminhos: string[] = [];
        for (const f of escolhidas) {
          const r2 = await api.enviarFoto(qual === 'retirada' ? 'retiradas' : 'entregas', f, r.numero);
          caminhos.push(r2.caminho);
        }

        estado.textContent = 'Registrando…';
        const lugar = await ondeEstou();
        const obs = bl.querySelector<HTMLInputElement>('[data-obs]')?.value.trim();

        await api.post(
          `/api/romaneios/${encodeURIComponent(r.numero)}/${qual}`,
          qual === 'retirada'
            ? { ...(caminhos[0] ? { fotoUrl: caminhos[0] } : {}), ...lugar }
            : { recebedor, fotos: caminhos, ...(obs ? { obs } : {}), ...lugar },
        );
        await carregar();
      } catch (e) {
        botao.disabled = false;
        estado.textContent = '';
        erro.textContent =
          e instanceof ErroApi
            ? e.message
            : 'Não consegui registrar. Confira o sinal e tente de novo.';
        erro.hidden = false;
      }
    }
  }

  /**
   * A localização, se o navegador der — e sem travar se não der.
   *
   * É opcional de propósito: a pessoa pode negar a permissão, o GPS pode não
   * pegar dentro de um galpão, e nada disso pode impedir o registro da entrega.
   * O comprovante fica sem o mapa; a entrega acontece.
   */
  function ondeEstou(): Promise<{ lat?: number; lng?: number }> {
    if (!navigator.geolocation) return Promise.resolve({});
    return new Promise((resolve) => {
      const desistir = setTimeout(() => resolve({}), 6000);
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          clearTimeout(desistir);
          resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        },
        () => {
          clearTimeout(desistir);
          resolve({});
        },
        { enableHighAccuracy: true, timeout: 5000 },
      );
    });
  }

  void carregar();
}
